import { Controller } from "@hotwired/stimulus"

// Generic controller for conditionally showing/hiding elements based on input values
// Usage:
// <div data-controller="visibility" 
//      data-visibility-rules-value='{"discharge": "hide", "intake": "show", "30_day": "show"}'>
//   <select data-action="change->visibility#evaluate">
//     <option value="intake">Intake</option>
//     <option value="discharge">Discharge</option>
//   </select>
//   <div data-visibility-target="element">
//     <!-- This will be shown/hidden based on the rules -->
//   </div>
// </div>
export default class extends Controller {
  static targets = ["element", "inverseElement"]
  static values = {
    rules: Object,          // Rules for visibility: { "value": "show|hide" }
    clearOnHide: Boolean,   // Whether to clear input values when hiding
    defaultState: String    // Default visibility state: "show" or "hide"
  }

  declare readonly elementTargets: HTMLElement[]
  declare readonly hasElementTarget: boolean
  declare readonly inverseElementTargets: HTMLElement[]
  declare readonly hasInverseElementTarget: boolean
  declare readonly rulesValue: Record<string, string>
  declare readonly clearOnHideValue: boolean
  declare readonly defaultStateValue: string
  declare readonly hasRulesValue: boolean

  connect(): void {
    // Set initial visibility state
    this.evaluate()
  }

  evaluate(event?: Event): void {
    const sourceElement = event 
      ? event.currentTarget as HTMLInputElement | HTMLSelectElement
      : this.findSourceElement()
    
    if (!sourceElement) {
      console.warn('Visibility controller: No source element found')
      return
    }

    const currentValue = this.getElementValue(sourceElement)
    const action = this.determineAction(currentValue)
    
    this.elementTargets.forEach(target => {
      this.applyVisibility(target, action)
    })

    if (this.hasInverseElementTarget) {
      const inverseAction = action === 'show' ? 'hide' : 'show'
      this.inverseElementTargets.forEach(target => {
        this.applyVisibility(target, inverseAction)
      })
    }
  }

  private findSourceElement(): HTMLInputElement | HTMLSelectElement | null {
    // Look for elements with data-action containing visibility#evaluate
    return this.element.querySelector<HTMLInputElement | HTMLSelectElement>(
      'input[data-action*="visibility#evaluate"], select[data-action*="visibility#evaluate"]'
    )
  }

  private getElementValue(element: HTMLInputElement | HTMLSelectElement): string {
    if (element.type === 'checkbox') {
      return element.checked ? 'checked' : 'unchecked'
    } else if (element.type === 'radio') {
      // For radio buttons, find the checked one in the group
      const checkedRadio = this.element.querySelector<HTMLInputElement>(
        `input[type="radio"][name="${element.name}"]:checked`
      )
      return checkedRadio?.value || ''
    }
    return element.value
  }

  private determineAction(value: string): 'show' | 'hide' {
    if (this.hasRulesValue && value in this.rulesValue) {
      return this.rulesValue[value] as 'show' | 'hide'
    }
    // If no rule matches, use default state
    return (this.defaultStateValue || 'show') as 'show' | 'hide'
  }

  private applyVisibility(element: HTMLElement, action: 'show' | 'hide'): void {
    if (action === 'hide') {
      element.classList.add('hidden')

      if (this.clearOnHideValue) {
        // Clear form inputs within the hidden element
        this.clearInputs(element)
      }
    } else {
      element.classList.remove('hidden')
    }
  }

  private clearInputs(container: HTMLElement): void {
    const inputs = container.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
      'input, textarea, select'
    )
    
    inputs.forEach(input => {
      if (input.type === 'checkbox' || input.type === 'radio') {
        (input as HTMLInputElement).checked = false
      } else {
        input.value = ''
      }
      
      // Dispatch change event to notify other controllers
      input.dispatchEvent(new Event('change', { bubbles: true }))
    })
  }
}
